import React, { useState, useRef, useEffect } from "react";
import { Box, Typography, TextField, Button } from "@mui/material";
// Using a simple + symbol instead of MUI icon

interface HSV {
  h: number; // 0-360
  s: number; // 0-100
  v: number; // 0-100
}

interface RGB {
  r: number;
  g: number;
  b: number;
}

interface ColorPickerProps {
  color: string;
  onChange: (color: string) => void;
  onClose?: () => void;
}

export const ColorPicker: React.FC<ColorPickerProps> = ({ color, onChange, onClose }) => {
  const [hsv, setHsv] = useState<HSV>({ h: 250, s: 65, v: 90 });
  const [opacity, setOpacity] = useState(100);
  const [hexInput, setHexInput] = useState(color);
  const [savedColors, setSavedColors] = useState<string[]>([
    "#FF5757",
    "#FF8A57", 
    "#FFD757",
    "#57FF8A",
    "#57FFD7",
    "#5757FF",
    "#FF57D7",
    "#FF5757",
    "#FF8A8A",
    "#D757FF",
    "#8A57FF",
    "#57D7FF",
    "#57FFD7",
    "#8AFF57",
  ]);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const hueCanvasRef = useRef<HTMLCanvasElement>(null);
  const opacityCanvasRef = useRef<HTMLCanvasElement>(null);
  const [isDraggingColor, setIsDraggingColor] = useState(false);
  const [isDraggingHue, setIsDraggingHue] = useState(false);
  const [isDraggingOpacity, setIsDraggingOpacity] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Convert HSV to RGB
  const hsvToRgb = (h: number, s: number, v: number): RGB => {
    s = s / 100;
    v = v / 100;
    const c = v * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = v - c;
    let r = 0, g = 0, b = 0;

    if (h >= 0 && h < 60) {
      r = c; g = x; b = 0;
    } else if (h >= 60 && h < 120) {
      r = x; g = c; b = 0;
    } else if (h >= 120 && h < 180) {
      r = 0; g = c; b = x;
    } else if (h >= 180 && h < 240) {
      r = 0; g = x; b = c;
    } else if (h >= 240 && h < 300) {
      r = x; g = 0; b = c;
    } else if (h >= 300 && h < 360) {
      r = c; g = 0; b = x;
    }

    return {
      r: Math.round((r + m) * 255),
      g: Math.round((g + m) * 255),
      b: Math.round((b + m) * 255),
    };
  };

  // Convert RGB to Hex
  const rgbToHex = (r: number, g: number, b: number): string => {
    return (
      "#" +
      [r, g, b]
        .map((x) => {
          const hex = x.toString(16);
          return hex.length === 1 ? "0" + hex : hex;
        })
        .join("")
        .toUpperCase()
    );
  };

  // Convert Hex to HSV
  const hexToHsv = (hex: string): HSV => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result) return hsv;

    const r = Number.parseInt(result[1], 16) / 255;
    const g = Number.parseInt(result[2], 16) / 255;
    const b = Number.parseInt(result[3], 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const diff = max - min;

    let h = 0;
    const s = max === 0 ? 0 : (diff / max) * 100;
    const v = max * 100;

    if (diff !== 0) {
      if (max === r) h = 60 * (((g - b) / diff) % 6);
      else if (max === g) h = 60 * ((b - r) / diff + 2);
      else if (max === b) h = 60 * ((r - g) / diff + 4);
    }

    if (h < 0) h += 360;

    return { h, s, v };
  };

  // Initialize component
  useEffect(() => {
    const initializeComponent = () => {
      const initialHsv = hexToHsv(color);
      setHsv(initialHsv);
      setHexInput(color);
      setIsInitialized(true);
    };

    // Add delay to ensure all canvas elements are ready
    const timeoutId = setTimeout(initializeComponent, 50);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [color]);

  // Update hex when HSV changes
  useEffect(() => {
    const rgb = hsvToRgb(hsv.h, hsv.s, hsv.v);
    const hex = rgbToHex(rgb.r, rgb.g, rgb.b);
    setHexInput(hex);
    onChange(hex);
  }, [hsv, onChange]);

  // Draw color picker canvas
  useEffect(() => {
    if (!isInitialized) return;

    const drawColorCanvas = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      const width = canvas.width;
      const height = canvas.height;

      // Clear canvas first
      ctx.clearRect(0, 0, width, height);

      // Draw hue background
      const hueGradient = ctx.createLinearGradient(0, 0, width, 0);
      hueGradient.addColorStop(0, `hsl(${hsv.h}, 100%, 50%)`);
      hueGradient.addColorStop(1, `hsl(${hsv.h}, 100%, 50%)`);
      ctx.fillStyle = hueGradient;
      ctx.fillRect(0, 0, width, height);

      // Draw white to transparent gradient (saturation)
      const whiteGradient = ctx.createLinearGradient(0, 0, width, 0);
      whiteGradient.addColorStop(0, "rgba(255, 255, 255, 1)");
      whiteGradient.addColorStop(1, "rgba(255, 255, 255, 0)");
      ctx.fillStyle = whiteGradient;
      ctx.fillRect(0, 0, width, height);

      // Draw transparent to black gradient (value)
      const blackGradient = ctx.createLinearGradient(0, 0, 0, height);
      blackGradient.addColorStop(0, "rgba(0, 0, 0, 0)");
      blackGradient.addColorStop(1, "rgba(0, 0, 0, 1)");
      ctx.fillStyle = blackGradient;
      ctx.fillRect(0, 0, width, height);
    };

    // Add small delay to ensure canvas is ready
    const timeoutId = setTimeout(drawColorCanvas, 10);
    return () => clearTimeout(timeoutId);
  }, [hsv.h, isInitialized]);

  // Draw hue slider
  useEffect(() => {
    if (!isInitialized) return;

    const drawHueCanvas = () => {
      const canvas = hueCanvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      const width = canvas.width;
      const height = canvas.height;

      // Clear canvas first
      ctx.clearRect(0, 0, width, height);

      const gradient = ctx.createLinearGradient(0, 0, width, 0);
      gradient.addColorStop(0, "rgb(255, 0, 0)");
      gradient.addColorStop(0.17, "rgb(255, 255, 0)");
      gradient.addColorStop(0.33, "rgb(0, 255, 0)");
      gradient.addColorStop(0.5, "rgb(0, 255, 255)");
      gradient.addColorStop(0.67, "rgb(0, 0, 255)");
      gradient.addColorStop(0.83, "rgb(255, 0, 255)");
      gradient.addColorStop(1, "rgb(255, 0, 0)");

      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);
    };

    // Add small delay to ensure canvas is ready
    const timeoutId = setTimeout(drawHueCanvas, 10);
    return () => clearTimeout(timeoutId);
  }, [isInitialized]);

  // Draw opacity slider
  useEffect(() => {
    if (!isInitialized) return;

    const drawOpacityCanvas = () => {
      const canvas = opacityCanvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      const width = canvas.width;
      const height = canvas.height;

      // Clear canvas first
      ctx.clearRect(0, 0, width, height);

      // Draw checkered background
      const checkSize = 4;
      for (let y = 0; y < height; y += checkSize) {
        for (let x = 0; x < width; x += checkSize) {
          ctx.fillStyle = (x / checkSize + y / checkSize) % 2 === 0 ? "#E5E7EB" : "#F9FAFB";
          ctx.fillRect(x, y, checkSize, checkSize);
        }
      }

      // Draw opacity gradient
      const rgb = hsvToRgb(hsv.h, hsv.s, hsv.v);
      const gradient = ctx.createLinearGradient(0, 0, width, 0);
      gradient.addColorStop(0, `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0)`);
      gradient.addColorStop(1, `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 1)`);

      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);
    };

    // Add small delay to ensure canvas is ready
    const timeoutId = setTimeout(drawOpacityCanvas, 10);
    return () => clearTimeout(timeoutId);
  }, [hsv, isInitialized]);

  const handleColorCanvasClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const s = (x / rect.width) * 100;
    const v = 100 - (y / rect.height) * 100;

    setHsv({ ...hsv, s, v });
  };

  const handleHueCanvasClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = hueCanvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const h = (x / rect.width) * 360;

    setHsv({ ...hsv, h });
  };

  const handleOpacityCanvasClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = opacityCanvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
    const newOpacity = Math.round((x / rect.width) * 100);

    setOpacity(newOpacity);
  };

  const handleHexInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setHexInput(value);

    if (/^#[0-9A-F]{6}$/i.test(value)) {
      const newHsv = hexToHsv(value);
      setHsv(newHsv);
    }
  };

  const handleAddColor = () => {
    if (!savedColors.includes(hexInput)) {
      setSavedColors([...savedColors, hexInput]);
    }
  };

  return (
    <Box sx={{ 
      width: '100%', 
      maxWidth: '320px', 
      padding: '20px', 
      backgroundColor: 'white',
      borderRadius: '12px',
      border: '1px solid #e5e7eb',
      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)'
    }}>
      {/* Color Picker Canvas */}
      <Box sx={{ position: 'relative', marginBottom: '12px' }}>
        <canvas
          ref={canvasRef}
          width={280}
          height={180}
          style={{
            width: '100%',
            height: '180px',
            borderRadius: '8px',
            cursor: 'crosshair'
          }}
          onClick={handleColorCanvasClick}
          onMouseDown={() => setIsDraggingColor(true)}
          onMouseUp={() => setIsDraggingColor(false)}
          onMouseMove={(e) => isDraggingColor && handleColorCanvasClick(e)}
        />
        {/* Color Picker Indicator */}
        <Box
          sx={{
            position: 'absolute',
            width: '16px',
            height: '16px',
            border: '2px solid white',
            borderRadius: '50%',
            pointerEvents: 'none',
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
            left: `${(hsv.s / 100) * 100}%`,
            top: `${(1 - hsv.v / 100) * 100}%`,
            transform: 'translate(-50%, -50%)',
          }}
        />
      </Box>

      {/* Hue Slider */}
      <Box sx={{ position: 'relative', marginBottom: '12px' }}>
        <canvas
          ref={hueCanvasRef}
          width={280}
          height={12}
          style={{
            width: '100%',
            height: '12px',
            borderRadius: '6px',
            cursor: 'pointer'
          }}
          onClick={handleHueCanvasClick}
          onMouseDown={() => setIsDraggingHue(true)}
          onMouseUp={() => setIsDraggingHue(false)}
          onMouseMove={(e) => isDraggingHue && handleHueCanvasClick(e)}
        />
        {/* Hue Slider Indicator */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            width: '16px',
            height: '16px',
            backgroundColor: 'white',
            border: '2px solid #d1d5db',
            borderRadius: '50%',
            pointerEvents: 'none',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
            left: `${(hsv.h / 360) * 100}%`,
            transform: 'translate(-50%, -50%)',
          }}
        />
      </Box>

      {/* Opacity Slider */}
      <Box sx={{ position: 'relative', marginBottom: '16px' }}>
        <canvas
          ref={opacityCanvasRef}
          width={280}
          height={12}
          style={{
            width: '100%',
            height: '12px',
            borderRadius: '6px',
            cursor: 'pointer'
          }}
          onClick={handleOpacityCanvasClick}
          onMouseDown={() => setIsDraggingOpacity(true)}
          onMouseUp={() => setIsDraggingOpacity(false)}
          onMouseMove={(e) => isDraggingOpacity && handleOpacityCanvasClick(e)}
        />
        {/* Opacity Slider Indicator */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            width: '16px',
            height: '16px',
            backgroundColor: 'white',
            border: '2px solid #d1d5db',
            borderRadius: '50%',
            pointerEvents: 'none',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
            left: `${opacity}%`,
            transform: 'translate(-50%, -50%)',
          }}
        />
      </Box>

      {/* Hex Input */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
          <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '12px' }}>Hex</Typography>
          <TextField
            value={hexInput}
            onChange={handleHexInputChange}
            size="small"
            sx={{
              flex: 1,
              '& .MuiOutlinedInput-root': {
                height: '32px',
                fontSize: '12px'
              }
            }}
            inputProps={{ maxLength: 7 }}
          />
        </Box>
        <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '12px' }}>{opacity}%</Typography>
      </Box>

      {/* Saved Colors */}
      <Box sx={{ borderTop: '1px solid #e5e7eb', paddingTop: '12px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
          <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '12px' }}>Saved colors:</Typography>
          <Button
            variant="text"
            size="small"
            onClick={handleAddColor}
            sx={{
              height: '24px',
              fontSize: '12px',
              color: '#3b82f6',
              '&:hover': {
                color: '#1d4ed8',
                backgroundColor: 'transparent'
              }
            }}
          >
            + Add
          </Button>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {savedColors.map((color, index) => (
            <Box
              key={index}
              sx={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                border: '2px solid #e5e7eb',
                cursor: 'pointer',
                backgroundColor: color,
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: '#9ca3af',
                  transform: 'scale(1.1)'
                }
              }}
              onClick={() => {
                setHexInput(color);
                const newHsv = hexToHsv(color);
                setHsv(newHsv);
              }}
              title={color}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};
